/**
 * Hotel Booking Review Selectors
 *
 * Memoized selectors for accessing hotel booking review state.
 * Using createSelector ensures components only re-render when selected data changes.
 *
 * NOTE: Coupon state is accessed via useHotelCoupon hook with inline selector.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';

// ============================================
// Base Selectors
// ============================================

const selectHotelBookingReviewState = (state: RootState) => state.hotels.bookingReview;

// ============================================
// Review Loading Selectors
// ============================================

export const selectIsHotelReviewLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isReviewLoading,
);

export const selectHotelReviewError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.reviewError,
);

// ============================================
// Policy Rules Selectors
// ============================================

export const selectIsPolicyRulesLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isPolicyRulesLoading,
);

export const selectPolicyRulesError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.policyRulesError,
);

export const selectPolicyRulesData = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.policyRulesData,
);

export const selectAllRules = createSelector(
  [selectPolicyRulesData],
  (policyRulesData) => policyRulesData?.allRules ?? [],
);

// ============================================
// Travellers Selectors (Backend-controlled)
// ============================================

/**
 * Select all travellers from Redux state.
 * This is backend-controlled - NEVER derive or compute state locally.
 */
export const selectTravellers = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.travellers,
);

/**
 * Select only selected travellers.
 * Uses the isSelected flag from backend response.
 */
export const selectSelectedTravellers = createSelector([selectTravellers], (travellers) =>
  travellers.filter((t) => t.isSelected),
);

/**
 * Select the primary traveller (if any is selected and marked as primary).
 */
export const selectPrimaryTraveller = createSelector(
  [selectTravellers],
  (travellers) => travellers.find((t) => t.isPrimary && t.isSelected) ?? null,
);

export const selectIsTravellerOperationLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isTravellerOperationLoading,
);

export const selectTravellerOperationError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.travellerOperationError,
);

// ============================================
// GST Operation Selectors
// ============================================

export const selectIsGstOperationLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isGstOperationLoading,
);

export const selectGstOperationError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.gstOperationError,
);

// ============================================
// PAN Operation Selectors
// ============================================

export const selectIsPanOperationLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isPanOperationLoading,
);

export const selectPanOperationError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.panOperationError,
);

export const selectCurrentPanNumber = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.currentPanNumber,
);

export const selectPanFromChat = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.panFromChat,
);

export const selectIsPanFromChatValidated = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isPanFromChatValidated,
);

// ============================================
// Country Codes Selectors
// ============================================

export const selectIsCountryCodesLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isCountryCodesLoading,
);

export const selectCountryCodesError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.countryCodesError,
);

export const selectCountryCodesData = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.countryCodesData,
);

export const selectCountryCodes = createSelector(
  [selectCountryCodesData],
  (countryCodesData) => countryCodesData?.countryCodes ?? [],
);

// ============================================
// Fare Summary Selectors
// ============================================

export const selectHotelFareSummary = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.fareSummary,
);

export const selectHotelReviewPrice = createSelector(
  [selectHotelFareSummary],
  (fareSummary) => fareSummary.reviewPrice,
);

export const selectHotelFareBreakup = createSelector(
  [selectHotelFareSummary],
  (fareSummary) => fareSummary.fareBreakup,
);

// ============================================
// Coupon Selectors
// ============================================

export const selectIsCouponValidating = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isCouponValidating,
);

export const selectCouponError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.couponError,
);

/**
 * Select the currently applied coupon code.
 * Single source of truth for both UI-click and chat-initiated coupon validations.
 */
export const selectAppliedCouponCode = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.appliedCouponCode,
);

// ============================================
// Update Price Selectors (PAN/TCS - loading/error only)
// ============================================

export const selectIsUpdatePriceLoading = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.isUpdatePriceLoading,
);

export const selectUpdatePriceError = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.updatePriceError,
);

// ============================================
// Chat Checkout Selectors
// ============================================

export const selectChatCheckoutUrl = createSelector(
  [selectHotelBookingReviewState],
  (bookingReview) => bookingReview.chatCheckoutUrl,
);
